'use client';

import { useMemo } from 'react';
import {
  ADDITIONAL_COMMON_BUSINESS_FEATURES_V3,
  ADDITIONAL_MAX_FEATURES_V3,
  ADDITIONAL_PRO_FEATURES_V3,
  ADDITIONAL_STANDARD_FEATURES_V3,
  ADDITIONAL_ULTRA_FEATURES_V3,
  PRO_FEATURES_V3,
  STANDARD_FEATURES_V3,
  MAX_FEATURES_V3,
  ULTRA_FEATURES_V3,
} from 'src/constants';
import styles from './PlanCardContent.module.scss';
import { PlanFamily, PlanKind } from 'src/types';
import { UserTier } from 'src/types/wallet/models/UserTier';
import { PlanCardContentItem } from 'src/pages/PaymentBetaPage/componentsV2/PlanCardContentItem';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';

interface PlanCardContentProps {
  planFamily: PlanFamily;
  planKind: PlanKind;
  planTier?: UserTier;
}

export const PlanCardContent = ({ planKind }: PlanCardContentProps) => {
  const features = useMemo(() => {
    if (
      planKind === PlanKind.MAX_05_ANNUALLY ||
      planKind === PlanKind.MAX_05_MONTHLY
    ) {
      return STANDARD_FEATURES_V3;
    }

    if (
      planKind === PlanKind.MAX_10_ANNUALLY ||
      planKind === PlanKind.MAX_10_MONTHLY ||
      planKind === PlanKind.BUSINESS_10_ANNUALLY ||
      planKind === PlanKind.BUSINESS_10_MONTHLY
    ) {
      return PRO_FEATURES_V3;
    }

    if (
      planKind === PlanKind.MAX_20_ANNUALLY ||
      planKind === PlanKind.MAX_20_MONTHLY ||
      planKind === PlanKind.BUSINESS_20_ANNUALLY ||
      planKind === PlanKind.BUSINESS_20_MONTHLY
    ) {
      return ULTRA_FEATURES_V3;
    }

    if (
      planKind === PlanKind.MAX_30_ANNUALLY ||
      planKind === PlanKind.MAX_30_MONTHLY ||
      planKind === PlanKind.BUSINESS_30_ANNUALLY ||
      planKind === PlanKind.BUSINESS_30_MONTHLY
    ) {
      return MAX_FEATURES_V3;
    }

    return [];
  }, [planKind]);

  const additionalFeatures = useMemo(() => {
    if (
      planKind === PlanKind.MAX_05_ANNUALLY ||
      planKind === PlanKind.MAX_05_MONTHLY
    ) {
      return ADDITIONAL_STANDARD_FEATURES_V3;
    }

    if (
      planKind === PlanKind.MAX_10_ANNUALLY ||
      planKind === PlanKind.MAX_10_MONTHLY
    ) {
      return ADDITIONAL_PRO_FEATURES_V3;
    }

    if (
      planKind === PlanKind.BUSINESS_10_ANNUALLY ||
      planKind === PlanKind.BUSINESS_10_MONTHLY
    ) {
      return [
        ...ADDITIONAL_PRO_FEATURES_V3,
        ...ADDITIONAL_COMMON_BUSINESS_FEATURES_V3,
      ];
    }

    if (
      planKind === PlanKind.MAX_20_ANNUALLY ||
      planKind === PlanKind.MAX_20_MONTHLY
    ) {
      return ADDITIONAL_ULTRA_FEATURES_V3;
    }

    if (
      planKind === PlanKind.BUSINESS_20_ANNUALLY ||
      planKind === PlanKind.BUSINESS_20_MONTHLY
    ) {
      return [
        ...ADDITIONAL_ULTRA_FEATURES_V3,
        ...ADDITIONAL_COMMON_BUSINESS_FEATURES_V3,
      ];
    }

    if (
      planKind === PlanKind.MAX_30_ANNUALLY ||
      planKind === PlanKind.MAX_30_MONTHLY
    ) {
      return ADDITIONAL_MAX_FEATURES_V3;
    }

    if (
      planKind === PlanKind.BUSINESS_30_ANNUALLY ||
      planKind === PlanKind.BUSINESS_30_MONTHLY
    ) {
      return [
        ...ADDITIONAL_MAX_FEATURES_V3,
        ...ADDITIONAL_COMMON_BUSINESS_FEATURES_V3,
      ];
    }

    return [];
  }, [planKind]);

  return (
    <div className={styles.root}>
      <div className={styles.titleWrapper}>
        <div>
          <h4 className={styles.additionalFeaturesTitle}>
            Chat & Image AI Agent
          </h4>
          <p className={styles.additionalFeaturesDescription}>
            Code, chat, brainstorm and search with your preferred AI Model
          </p>
        </div>
        <p className={styles.subtitle}>Unlimited</p>
      </div>

      {features.map((item, index) => (
        <PlanCardContentItem key={index} {...item} />
      ))}

      <TextDivider />

      {additionalFeatures.map((item, index) => (
        <PlanCardContentItem key={index} {...item} />
      ))}
    </div>
  );
};
